import type { DefaultTagDefinition } from "../../../../types/tags";
import type { ProspectSortColumn } from "../../../../types/sort";
import { tagSortColumn } from "../../../../types/sort";

/**
 * The single source of truth for what columns the players table renders, in what
 * order.
 *
 * Before this existed the tag-column expression
 * (`filter(≠"Class").sort(displayOrder)`) was written out three times — in the
 * header, in the reorder-mode row, and in the static row. Three independent
 * copies of one decision, and because all three emitted the same *count* of
 * cells, a mismatch between them would not throw. It would quietly render each
 * value under the wrong heading. The header and both row bodies now map over one
 * array, so that class of bug is gone by construction rather than by discipline.
 */

/** Structural columns. Never hidden, never reordered — each carries a capability. */
export const COL_DRAG = "drag";           // reorder mode drags rows by this handle
export const COL_SELECT = "select";       // bulk selection
export const COL_NAME = "name";           // the row's link to the prospect
export const COL_ACTIONS = "actions";     // favorite, video, note

export const COL_POSITION = "position";
export const COL_GRAD_YEAR = "gradYear";
export const COL_JERSEY = "jerseyNumber";
export const COL_SCHOOL = "school";

export const TAG_COLUMN_PREFIX = "tag:";

/** Key for a tag category's column. */
export const tagColumnKey = (tagName: string): string =>
  `${TAG_COLUMN_PREFIX}${tagName}`;

export type PlayersColumnKind = "structural" | "field" | "tag";

export type PlayersColumn = {
  key: string;
  label: string;
  kind: PlayersColumnKind;
  /** False for structural columns; hiding one of those removes a capability. */
  hideable: boolean;
  /** Absent for structural columns, which are not sortable. */
  sortColumn?: ProspectSortColumn;
  /** Tag category name. Set only when `kind` is "tag". */
  tagName?: string;
  /** Valid option values, used to filter out stale tag values. Tag columns only. */
  options?: string[];
  /**
   * Off until a coach turns it on. Only applies while the column is missing from the saved
   * order: turning it on writes it into the order, so from then on the layout decides.
   */
  defaultHidden?: boolean;
};

/** A user's saved layout: the order of configurable columns, and which are hidden. */
export type PlayersColumnLayout = {
  order: string[];
  hidden: string[];
};

export const EMPTY_COLUMN_LAYOUT: PlayersColumnLayout = { order: [], hidden: [] };

/*
  Position is fixed for these four: drag/select/name lead, actions trails. They
  are excluded from the configurable middle rather than merely marked
  un-hideable, so a saved order can never move them.
*/
const LEADING_COLUMNS: PlayersColumn[] = [
  { key: COL_DRAG, label: "", kind: "structural", hideable: false },
  { key: COL_SELECT, label: "", kind: "structural", hideable: false },
  {
    key: COL_NAME,
    label: "Name",
    kind: "structural",
    hideable: false,
    sortColumn: "name",
  },
];

const TRAILING_COLUMNS: PlayersColumn[] = [
  { key: COL_ACTIONS, label: "", kind: "structural", hideable: false },
];

const FIELD_COLUMNS: PlayersColumn[] = [
  {
    key: COL_POSITION,
    label: "POS",
    kind: "field",
    hideable: true,
    sortColumn: "position",
  },
  {
    // "Class", not "Grad Year": the cell carries a graduation year for most
    // players and a year of junior college ("JC 2") for a JUCO player, and a
    // header reading "Grad Year" over "JC 2" is wrong. "Class" names both, and
    // it is what mobile's iPad table has always called this column. The
    // `sortColumn` stays `gradYear` — the server sorts on the stored scalar.
    key: COL_GRAD_YEAR,
    label: "Class",
    kind: "field",
    hideable: true,
    sortColumn: "gradYear",
  },
  {
    key: COL_JERSEY,
    label: "#",
    kind: "field",
    hideable: true,
    sortColumn: "jerseyNumber",
  },
  {
    // Off by default: most coaches never asked for it, and a new column must not appear
    // for everyone who already arranged their table. Not sortable; the server has no
    // school sort.
    key: COL_SCHOOL,
    label: "School",
    kind: "field",
    hideable: true,
    defaultHidden: true,
  },
];

/**
 * The tag categories that get a column.
 *
 * The default "Class" tag duplicates the built-in Class column and nothing
 * populates it, so it is excluded — the same exclusion the table applied before
 * this module existed.
 */
const tagColumns = (
  tagDefinitions: DefaultTagDefinition[]
): PlayersColumn[] =>
  tagDefinitions
    .slice()
    .filter((def) => def.tagName !== "Class")
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((def) => ({
      key: tagColumnKey(def.tagName),
      label: def.tagName,
      kind: "tag" as const,
      hideable: true,
      sortColumn: tagSortColumn(def.tagName),
      tagName: def.tagName,
      options: def.options,
    }));

/**
 * Every column a user may show, hide, or reorder, in default order: the built-in
 * fields first, then the organization's tag categories by `displayOrder`.
 *
 * This is also the order a user with no saved layout sees, so it must keep
 * matching what the table rendered before the feature existed.
 */
export const configurableColumns = (
  tagDefinitions: DefaultTagDefinition[]
): PlayersColumn[] => [...FIELD_COLUMNS, ...tagColumns(tagDefinitions)];

/**
 * Arrange the configurable columns by a saved layout and work out which are
 * hidden. Shared by the table and the column settings panel so the two cannot
 * disagree about what a coach sees.
 *
 * Three rules earn their keep here:
 *
 * - A column absent from the saved order is **appended**, not dropped. A tag
 *   category created after a coach arranged their columns would otherwise never
 *   appear, and they would reasonably report it as the new tag not saving.
 * - An appended column marked `defaultHidden` is hidden. That covers a new user
 *   (empty order), an older saved layout that predates the column, and a reset.
 *   Turning it on writes it into the order, so it then shows like any other.
 * - A saved key that no longer matches a column is ignored, so renaming or
 *   deleting a tag category cannot corrupt a layout. Same approach as
 *   `rehydrateTeamChips`.
 */
export const resolveColumnLayout = (
  columns: PlayersColumn[],
  layout: PlayersColumnLayout
): { ordered: PlayersColumn[]; hidden: Set<string> } => {
  const byKey = new Map(columns.map((c) => [c.key, c]));

  // Deduped while mapping: a saved order that repeats a key would otherwise
  // emit that column twice, colliding React keys and showing it twice.
  const seen = new Set<string>();
  const ordered: PlayersColumn[] = [];
  for (const key of layout.order) {
    const column = byKey.get(key);
    if (!column || seen.has(key)) continue;
    seen.add(key);
    ordered.push(column);
  }

  const appended = columns.filter((c) => !seen.has(c.key));

  const hidden = new Set(layout.hidden);
  for (const column of appended) {
    if (column.defaultHidden) hidden.add(column.key);
  }

  return { ordered: [...ordered, ...appended], hidden };
};

const applyLayout = (
  columns: PlayersColumn[],
  layout: PlayersColumnLayout
): PlayersColumn[] => {
  const { ordered, hidden } = resolveColumnLayout(columns, layout);
  return ordered.filter((c) => !hidden.has(c.key));
};

/**
 * The columns to render, in render order: pinned leading columns, the user's
 * arranged and filtered middle, then the pinned actions column.
 *
 * With no layout this returns exactly the pre-feature column set.
 */
export const buildPlayersColumns = (
  tagDefinitions: DefaultTagDefinition[],
  layout: PlayersColumnLayout = EMPTY_COLUMN_LAYOUT
): PlayersColumn[] => [
  ...LEADING_COLUMNS,
  ...applyLayout(configurableColumns(tagDefinitions), layout),
  ...TRAILING_COLUMNS,
];

/**
 * True when `sortColumn` belongs to a column the layout hides.
 *
 * Sort state lives in the URL and survives a re-parse, so hiding the sorted
 * column without clearing the sort leaves the table genuinely sorted by a
 * column that has no header and no arrow — sorted with nothing saying so.
 */
export const isSortedColumnHidden = (
  sortColumn: ProspectSortColumn | null,
  tagDefinitions: DefaultTagDefinition[],
  layout: PlayersColumnLayout
): boolean => {
  if (!sortColumn) return false;

  const visible = buildPlayersColumns(tagDefinitions, layout);
  if (visible.some((c) => c.sortColumn === sortColumn)) return false;

  // Only report a *hidden* column, not one this organization never had — an
  // unknown sort key is the URL-parsing concern in types/sort.ts, not this one.
  return configurableColumns(tagDefinitions).some(
    (c) => c.sortColumn === sortColumn
  );
};
